import React from 'react';
import { Button, CircularProgress, DialogContent, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../Redux/store';
import { fetchFiles, fetchFileData, setCurrentFileIndex } from '../../../Redux/fileSlice';

interface ProjectManagementFileTabsProps {
  open?: boolean;
  loading?: boolean;
  error?: string | null;
  imageSrc: string | null;
  apiPath?: string;
  getFileType?: (fileName: string) => string;
}

const ProjectManagementFileTabs: React.FC<ProjectManagementFileTabsProps> = ({
  imageSrc,
  apiPath,
  getFileType,
  open,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { files, currentFileIndex, loading, error } = useSelector((state: RootState) => state.files);

  const currentFile = files[currentFileIndex];

  const getFileSizeInBytes = (base64: string): number => {
    const padding = (base64.match(/=+$/) || [''])[0].length;
    return (base64.length * 3) / 4 - padding;
  };

const extractOriginalFileName = (fileName: string): string => {
  // Split on underscores and assume the last segment is the original name
  const parts = fileName.split('_');
  return parts.slice(-1).join('_'); // Or adjust this as needed
};



  const handleDownload = (fileData: string, fileName: string, fileType: string) => {
    if (!fileData) {
      console.error("No file data available to download.");
      return;
    }

    try {
      const byteCharacters = atob(fileData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: `application/${fileType}` });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // a.download = fileName;
      a.download = extractOriginalFileName(currentFile.file_name);

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  React.useEffect(() => {
    if (open && apiPath) {
      dispatch(fetchFiles(apiPath))
        .unwrap()
        .then((files) => {
          if (files.length > 0) {
            dispatch(fetchFileData({ docPath: files[0].doc_path, index: 0 }));
            dispatch(setCurrentFileIndex(0));
          }
        })
        .catch((err) => {
          console.error('Failed to fetch files:', err);
        });
    }
  }, [open, apiPath, dispatch]);

  return (
    <DialogContent sx={{ overflowY: 'hidden' }}>
      {loading ? (
        <div style={{ textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading files...
          </Typography>
        </div>
      ) : error ? (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      ) : files.length > 0 ? (
        <>
          {currentFile && (
            <>
              {getFileType && getFileType(currentFile.file_name) === 'pdf' ? (
                getFileSizeInBytes(currentFile.fileData || '') > 1 * 1024 * 1024 ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      PDF file is too large to preview. Please download to view.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        handleDownload(currentFile.fileData ?? '', currentFile.file_name, 'pdf')
                      }
                    >
                      Download PDF
                    </Button>
                  </>
                ) : (
                  <iframe
                    src={`data:application/pdf;base64,${currentFile.fileData}`}
                    width="100%"
                    height="500px"
                    title="PDF Preview"
                  />
                )
              ) : getFileType && getFileType(currentFile.file_name) === 'image' ? (
                <img
                  src={`data:image/png;base64,${currentFile.fileData}`}
                  alt={currentFile.file_name}
                  style={{ width: '100%', height: 'auto', maxHeight: '500px', objectFit: 'contain' }}
                />
              ) : (
                <>
                  <Typography variant="body2">
                    Preview not available for this file type. Please download to view.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={() =>
                      handleDownload(
                        currentFile.fileData ?? '',
                        currentFile.file_name,
                        getFileType ? getFileType(currentFile.file_name) : 'octet-stream'
                      )
                    }
                  >
                    Download File
                  </Button>
                </>
              )}
            </>
          )}
        </>
      ) : (
        <Typography variant="body2">No files available.</Typography>
      )}
    </DialogContent>
  );
};

export default ProjectManagementFileTabs;
